import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { IMG } from "@/data/images";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaCode, setMfaCode] = useState("");
  const [mfaRequired, setMfaRequired] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password, mfaCode);
      toast.success("Welcome back");
      navigate("/portal");
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail);
      if (detail === "MFA_REQUIRED") { setMfaRequired(true); setError("Enter your authentication code to continue."); }
      else setError(detail || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="relative hidden lg:block overflow-hidden grain">
        <img src={IMG.craftHands} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-bb-bg via-bb-bg/70 to-bb-bg/40" />
        <div className="absolute bottom-16 left-12 right-12 z-10">
          <h2 className="font-serif text-4xl leading-tight">The diamond remains yours. Its story becomes verifiable.</h2>
          <p className="mt-4 text-sm text-bb-silver/60 max-w-md">Secure industry access for licensed manufacturers, dealers, brokers, laboratories and technology providers.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-24 bg-bb-bg">
        <div className="w-full max-w-sm">
          <Link to="/" className="flex items-center gap-3 mb-10">
            <img src="/logo-mark.png" alt="" className="h-10 w-auto" />
            <span className="font-serif text-lg tracking-[0.18em]">BOTSWANA BRILLIANCE</span>
          </Link>
          <h1 className="font-serif text-4xl">Industry Login</h1>
          <p className="text-sm text-bb-silver/55 mt-2">Access your secure participant dashboard.</p>

          {error && <div data-testid="login-error" className="mt-6 rounded-lg border border-rose-400/30 bg-rose-400/10 px-4 py-3 text-sm text-rose-200">{error}</div>}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <Field label="Email" testid="login-email" type="email" value={email} onChange={setEmail} placeholder="you@company.com" />
            <Field label="Password" testid="login-password" type="password" value={password} onChange={setPassword} placeholder="••••••••" />
            {mfaRequired && (
              <Field label="Authentication code" testid="login-mfa" value={mfaCode} onChange={setMfaCode} placeholder="6-digit code" />
            )}
            <button data-testid="login-submit-btn" disabled={loading} className="btn-gold rounded-full w-full py-3.5 text-sm mt-2">
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <p className="mt-6 text-sm text-bb-silver/55">
            New participant? <Link to="/register" className="text-bb-gold hover:underline">Apply to Participate</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export function Field({ label, testid, type = "text", value, onChange, placeholder }) {
  return (
    <div>
      <label className="label-eyebrow">{label}</label>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 text-sm outline-none focus:border-bb-gold/50 transition-colors duration-300"
      />
    </div>
  );
}
